/**
 * AGI-428: the two client-side marks that decide whether the Meta-pricing
 * detail opens BY ITSELF. Neither is an acknowledgment, and neither touches
 * the bell.
 *
 * 🔴 ONE FACT, ONE PLACE (docs/solutions/logic-errors/one-fact-two-storage-
 * locations-partial-fix-diverges-consumers.md). "No volver a mostrar" lives
 * here and nowhere else: not in `AlertCenter.storage`'s acknowledgments (those
 * clear the bell row, which this must NOT do) and not on the backend.
 *
 * 🔴 AND IT STAYS CLIENT-SIDE. The repo's rule that a persistent flag may only
 * be written after the backend write it represents succeeds
 * (docs/solutions/logic-errors/localstorage-flag-set-before-backend-write-
 * confirms.md) does NOT apply: this mark represents no backend write at all.
 * It is a preference about an automatic modal, so "fix" it by sending it to a
 * server and you have invented a failure mode where a network blip re-opens a
 * modal the user switched off.
 *
 * Two marks, two lifetimes:
 *
 *  - PERSISTENT (localStorage), per workspace AND user, with the versioned
 *    read-modify-write shape of `NewsCard.storage`. Scoped by both ids because
 *    two people share a browser more often than anyone expects, and one
 *    owner's "no volver a mostrar" must not silence the notice for the other.
 *  - PER SESSION (sessionStorage), same key shape. A new TAB is a new session,
 *    which is the point: opening Leadsales again is when the user expects to
 *    be told, while a reload is not. Accepted tradeoff (plan, KTD): three tabs
 *    opened at once show it three times.
 *
 * Every access is wrapped: Safari private mode throws on read as well as on
 * write, and the correct degradation is "no mark" (the modal opens) rather
 * than a crash on boot.
 */

import type { tMetaPricingMessage } from 'src/alerts/MetaPricingNotice.types';

/**
 * One pair of prefixes per message (AGI-428, message 2 on 25-sep, message 3
 * on 6-oct). Message 1 keeps the prefixes it shipped with: renaming them would
 * bring the modal back to everyone who already switched it off. Each later
 * message gets its own, so what someone did with an earlier one cannot
 * silence a message they have not seen.
 */
const KEY_PREFIXES: Record<
  tMetaPricingMessage,
  { persistent: string; session: string }
> = {
  impact: {
    persistent: 'meta_pricing_notice_',
    session: 'meta_pricing_notice_seen_',
  },
  tips: {
    persistent: 'meta_pricing_tips_notice_',
    session: 'meta_pricing_tips_notice_seen_',
  },
  balance: {
    persistent: 'meta_pricing_balance_notice_',
    session: 'meta_pricing_balance_notice_seen_',
  },
};

export type tMetaPricingNoticeScope = {
  workspaceId: string;
  userId: string;
  /** Which message the marks belong to. Absent means message 1. */
  message?: tMetaPricingMessage;
};

export type tMetaPricingNoticeState = {
  v: 1;
  /** Epoch ms of the "no volver a mostrar" tick, absent while unticked. */
  dismissedForeverAt?: number;
};

export function getMetaPricingNoticeStorageKey(
  scope: tMetaPricingNoticeScope,
): string {
  const prefix = KEY_PREFIXES[scope.message ?? 'impact'].persistent;
  return `${prefix}${scope.workspaceId}_${scope.userId}`;
}

export function getMetaPricingNoticeSessionKey(
  scope: tMetaPricingNoticeScope,
): string {
  const prefix = KEY_PREFIXES[scope.message ?? 'impact'].session;
  return `${prefix}${scope.workspaceId}_${scope.userId}`;
}

function safeGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(
  storage: Storage | undefined,
  key: string,
  value: string,
): boolean {
  try {
    storage?.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(storage: Storage | undefined, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* best-effort cleanup */
  }
}

/**
 * Resolved per call rather than captured at module load: jsdom swaps the
 * globals between tests, and a captured reference would keep pointing at a
 * store the test already replaced.
 */
function localStore(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function sessionStore(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function freshState(): tMetaPricingNoticeState {
  return { v: 1 };
}

/**
 * Read fresh, immediately before every write. A parked tab holding an
 * in-memory snapshot must not resurrect a state another tab has moved on from
 * (the `NewsCard.storage` rule, same reason).
 */
export function readMetaPricingNoticeState(
  scope: tMetaPricingNoticeScope,
): tMetaPricingNoticeState {
  if (!scope.workspaceId || !scope.userId) return freshState();

  const key = getMetaPricingNoticeStorageKey(scope);
  const raw = safeGet(localStore(), key);
  if (raw === null) return freshState();

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupted JSON: clean up so later reads stop re-parsing garbage.
    safeRemove(localStore(), key);
    return freshState();
  }

  if (!parsed || typeof parsed !== 'object') {
    safeRemove(localStore(), key);
    return freshState();
  }

  const state = parsed as Record<string, unknown>;
  // No predecessor shape exists today, but a foreign or future payload must
  // not be trusted into a boolean the modal reads.
  if (state.v !== 1) {
    safeRemove(localStore(), key);
    return freshState();
  }

  const dismissedForeverAt =
    typeof state.dismissedForeverAt === 'number' &&
    Number.isFinite(state.dismissedForeverAt)
      ? state.dismissedForeverAt
      : undefined;

  return dismissedForeverAt === undefined ? { v: 1 } : { v: 1, dismissedForeverAt };
}

export function isMetaPricingNoticeDismissedForever(
  scope: tMetaPricingNoticeScope,
): boolean {
  return readMetaPricingNoticeState(scope).dismissedForeverAt !== undefined;
}

export type tSetMetaPricingDismissalInput = tMetaPricingNoticeScope & {
  dismissed: boolean;
};

/**
 * Ticking and unticking are the same operation, because the checkbox is a
 * checkbox: a user who ticks it and changes their mind before closing has to
 * be able to. Returns whether the write actually landed, so a caller in
 * private mode can keep the in-memory state honest instead of claiming a
 * preference that was never stored.
 */
export function setMetaPricingNoticeDismissedForever(
  input: tSetMetaPricingDismissalInput,
): boolean {
  const scope: tMetaPricingNoticeScope = {
    workspaceId: input.workspaceId,
    userId: input.userId,
    message: input.message,
  };
  if (!scope.workspaceId || !scope.userId) return false;

  const current = readMetaPricingNoticeState(scope);
  const next: tMetaPricingNoticeState = input.dismissed
    ? { ...current, v: 1, dismissedForeverAt: Date.now() }
    : { v: 1 };

  return safeSet(
    localStore(),
    getMetaPricingNoticeStorageKey(scope),
    JSON.stringify(next),
  );
}

export function isMetaPricingNoticeSeenThisSession(
  scope: tMetaPricingNoticeScope,
): boolean {
  if (!scope.workspaceId || !scope.userId) return false;
  return safeGet(sessionStore(), getMetaPricingNoticeSessionKey(scope)) === '1';
}

/**
 * Written the moment the detail is shown, by whichever path showed it. The
 * bell row counts: a user who opened the notice from the bell and closed it
 * has been told, and having the automatic modal appear afterwards would read
 * as the app not having noticed.
 */
export function markMetaPricingNoticeSeenThisSession(
  scope: tMetaPricingNoticeScope,
): void {
  if (!scope.workspaceId || !scope.userId) return;
  safeSet(sessionStore(), getMetaPricingNoticeSessionKey(scope), '1');
}
